import React, { useMemo } from 'react';
import { fetchLookupData, fetchLookupOption, fetchCustomerLookupOption, fetchEntityLookupOption, fetchProductLookupOption, fetchWorkSaleLookupOption, fetchPurchaseOrderLookupOption, fetchWarehousePurchaseOrderLookupOption, fetchProductPurchaseOrderLookupOption, fetchWarehouseProductLookupOption, fetchCustomerSupplierLookupOption } from 'api/basicLookupField/basicLookupApi';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';
import BasicLookupField from '@markinson/uicomponents-v2/BasicLookupField';
import { DEFAULT_BATCH_SIZE, FIXED_DEFAULT_TYPE_BATCH_SIZE, IBasicLookupActionFieldProps, ILookupFieldColumnEx, IScopeOptions, lookupConstants } from './constants';

const lookupSearchApi = (lookupName: string, params: any, searchScreenAPIParms: any) => (searchText: string | undefined, batchSize: number, batchPage: number, exactMatch?: boolean, fromSearchScreen?: boolean) => {
  return fetchLookupData({ lookupName, SearchText: searchText, BatchSize: batchSize, BatchPage: batchPage, CheckExactMatch: exactMatch, ...params ? params : {}, ...((fromSearchScreen && searchScreenAPIParms) ? searchScreenAPIParms : {}) });
};

const isWarehouseSupplier = (scopeOptions, params) => {
  return scopeOptions.isWarehouseSupplierScoped && params.Supplier && params.Warehouse;
};

const isWarehouseProduct = (scopeOptions, params) => {
  return scopeOptions.isWarehouseProductScoped && params.ProductId && (params.Warehouse);
};

const findOption = (lookupName: string, scopeOptions: IScopeOptions, params: any) => (value: string | undefined) => {
  // tslint:disable: cyclomatic-complexity
  if (isWarehouseProduct(scopeOptions, params)) {
    return fetchWarehouseProductLookupOption({ lookupName, value, Product: params.ProductId || '', Warehouse: params.Warehouse || '' });
  } else if (isWarehouseSupplier(scopeOptions, params)) {
    return fetchWarehousePurchaseOrderLookupOption({ lookupName, value, Supplier: params.Supplier || '', Warehouse: params.Warehouse || '' });
  } else if (scopeOptions.isCustomerSupplierScoped && (params.CustomerId || params.Supplier)) {
    return fetchCustomerSupplierLookupOption({ lookupName, value, CustomerId: params.CustomerId || 0, Supplier: params.Supplier || '' });
  } else if (scopeOptions.isCustomerScoped && params.CustomerId) {
    return fetchCustomerLookupOption({ lookupName, value, CustomerId: params.CustomerId || 0 });
  } else if (scopeOptions.isWorksaleScoped && params.WorksaleID) {
    return fetchWorkSaleLookupOption({ lookupName, value, WorksaleId: params.WorksaleID || '' });
  } else if (scopeOptions.isProductScoped && params.ProductId) {
    return fetchProductLookupOption({ lookupName, value, ProductId: params.ProductId || '' });
  } else if (scopeOptions.isEntityScoped && params.Entity) {
    return fetchEntityLookupOption({ lookupName, value, SalesEntity: params.Entity || '' });
  } else if (scopeOptions.isSupplierScoped && params.Supplier) {
    return fetchPurchaseOrderLookupOption({ lookupName, value, Supplier: params.Supplier || '' });
  } else if (scopeOptions.isProductWarehouseSupplierScoped && (params.Supplier && params.Warehouse && params.ProductId)) {
    return fetchProductPurchaseOrderLookupOption({ lookupName, value, Supplier: params.Supplier || '', Warehouse: params.Warehouse, ProductId: params.ProductId });
  } else {
    return fetchLookupOption({ lookupName, value });
  }
};

const BasicLookupActionField = (props: Omit<IBasicLookupActionFieldProps, 'classes' | 'onFindOption' | 'onSearch'>, ref: React.Ref<IBasicLookupFieldHandle>) => {

  const {
    lookupName,
    value,
    params = {},
    isCustomerSupplierScoped = false,
    isCustomerScoped = false,
    isWorksaleScoped = false,
    isProductScoped = false,
    isEntityScoped = false,
    isSupplierScoped = false,
    isWarehouseSupplierScoped = false,
    isWarehouseProductScoped = false,
    isWarehouseProductBinScoped = false,
    isWidgetTypeScoped = false,
    isLookupTypeFixed = false,
    isProductWarehouseSupplierScoped = false,
    isDashboardTypeScoped = false,
    column1Field,
    column2Field,
    valueField,
    displayField,
    descriptionField,
    fieldRef,
    setFieldAsyncError,
    onSelectedItemChange,
    onChange,
    onValueChange,
    onInvalidChange,
    isV2,
    isV3,
    searchScreenAPIParms,
    ...restProps
  } = props;

  const SearchApi = useMemo(() => lookupSearchApi(lookupName, params, searchScreenAPIParms), [lookupName, params, searchScreenAPIParms]);
  const findOptionApi = useMemo(
    () => {
      return findOption(
        lookupName,
        {
          isCustomerSupplierScoped,
          isCustomerScoped,
          isWorksaleScoped,
          isProductScoped,
          isEntityScoped,
          isWidgetTypeScoped,
          isSupplierScoped,
          isWarehouseSupplierScoped,
          isWarehouseProductScoped,
          isWarehouseProductBinScoped,
          isProductWarehouseSupplierScoped,
          isDashboardTypeScoped
        },
        params
      );
    },
    [
      lookupName,
      isCustomerSupplierScoped,
      isCustomerScoped,
      isWorksaleScoped,
      isProductScoped,
      isEntityScoped,
      isWidgetTypeScoped,
      isSupplierScoped,
      isWarehouseSupplierScoped,
      isWarehouseProductScoped,
      isWarehouseProductBinScoped,
      isProductWarehouseSupplierScoped,
      isDashboardTypeScoped,
      params
    ]);

  const lookupConstant = lookupConstants[lookupName];
  const isTabbedLookup = props?.isTabbedLookup || lookupConstant?.tabbedLookup?.isTabbedLookup || false;
  const showSummaryPanel = props?.showSummaryPanel || lookupConstant?.tabbedLookup?.showSummaryPanel || false;
  const shouldEnterBehaveAsTab = props?.shouldEnterBehaveAsTab || lookupConstant?.tabbedLookup?.shouldEnterBehaveAsTab || false;
  const columns = lookupConstant?.columns || null;
  const dialogStyle = lookupConstant?.dialogStyle || null;
  const hideColumnHeaders = lookupConstant?.hideColumnHeaders;
  const disableGridColumnLines = lookupConstant?.disableGridColumnLines;
  const dataPrefix = lookupConstant?.dataPrefix || null;

  const filterColumnsByVersion = (cols: ILookupFieldColumnEx[]): ILookupFieldColumnEx[] => {
    return cols.filter(({ hideInV2, hideInV3 }) => {
      return !(isV3 && hideInV3) && !(isV2 && hideInV2);
    });
  };

  return (
    <BasicLookupField
      {...restProps}
      innerRef={fieldRef ? fieldRef : ref}
      value={value as string}
      onFindOption={findOptionApi}
      batchSize={isLookupTypeFixed ? FIXED_DEFAULT_TYPE_BATCH_SIZE : DEFAULT_BATCH_SIZE}
      valueField={valueField || 'Code'}
      descriptionField={descriptionField || 'Description'}
      displayField={displayField || 'Display'}
      column1Field={column1Field || 'FormattedValue'}
      column2Field={column2Field || 'Description'}
      lookupOverrides={
        props?.lookupOverrides ? props?.lookupOverrides : lookupConstants[lookupName] ?
          {
            columns: columns ? filterColumnsByVersion(columns) : null,
            dialogStyle: dialogStyle || null,
            hideColumnHeaders: hideColumnHeaders,
            disableGridColumnLines: disableGridColumnLines,
            dataPrefix: dataPrefix || null,
          } : null}
      onSelectedItemChange={(item, inputText) => {
        if (onChange) {
          const { disableOptionsLookup, suppressValidation } = props || {};
          const selectedValue = !!(disableOptionsLookup && suppressValidation) ? inputText : item?.Code ?? inputText;
          onChange(selectedValue);
        }
        if (onSelectedItemChange) {
          onSelectedItemChange(item, inputText);
        }
      }}
      onInvalidChange={(invalid: boolean, invalidValue) => {
        if (onInvalidChange) {
          onInvalidChange(invalid, invalidValue);
        }
        if (setFieldAsyncError) {
          setFieldAsyncError(props.name, invalid ? 'Invalid Value' : null);
        }
      }}
      onChange={onValueChange}
      onSearch={SearchApi}
      isTabbedLookup={isTabbedLookup}
      showSummaryPanel={showSummaryPanel}
      shouldEnterBehaveAsTab={shouldEnterBehaveAsTab}
    />
  );
};

export default React.memo(React.forwardRef(BasicLookupActionField));
